import React from "react";
import { Box, Container, Grid, Typography, Button } from "@mui/material";
import { useT } from "contexts/TranslationContext";

const PRIMARY_GREEN = '#34986c';
const LIGHT_GREEN_BORDER = '#d1e7c5';

// --- Pricing Card Component ---
const PricingCard = ({ plan }) => {
    const t = useT();
    return (
        <Box
            sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                border: '1px solid',
                borderColor: plan.index === 2 ? PRIMARY_GREEN : LIGHT_GREEN_BORDER,
                borderRadius: '8px',
                bgcolor: 'white',
                height: '100%',
            }}
        >
            {/* Plan Title */}
            <Typography
                component="h3"
                sx={{ fontWeight: 700, color: 'grey.900', mb: 0.5 }}
                variant="h6" 
            >
                {plan.title}
            </Typography>

            {/* Plan Subtitle */}
            <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ color: 'grey.600', mb: 4 }}
            >
                {plan.subtitle}
            </Typography>

            {/* Price */}
            <Typography
                variant="h3"
                component="p"
                sx={{ 
                    fontWeight: 800, 
                    color: 'grey.900', 
                    fontSize: { xs: '3rem', sm: '3.5rem' },
                    mb: 3
                }}
            >
                {plan.price}
            </Typography>
            
            {/* Features List */}
            <Box mb={4} flexGrow={1}>
                {plan.features.map((feature, index) => (
                    <Typography 
                        key={index} 
                        variant="body2" 
                        sx={{ color: 'grey.700', mb: 1.5 }}
                    >
                        {feature}
                    </Typography>
                ))}
            </Box>

            {/* Action Button */}
            <Button
                variant="outlined"
                size="large"
                href={`register/${plan.type}`}
                sx={{
                    borderColor: PRIMARY_GREEN,
                    color: PRIMARY_GREEN,
                    '&:hover': {
                        borderColor: '#2d845e',
                        bgcolor: 'rgba(52, 152, 108, 0.05)',
                    },
                    width: '100%',
                    mt: 'auto',
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 600,
                    textTransform: 'none',
                }}
                aria-label={`Select ${plan.title} plan`}
            >
                {t("plans.select")}
            </Button>
        </Box>
    );
};

// --- Pricing Section Component ---
const PricingSection = () => {
    const t = useT();
    const plans = [
        {
            index: 1,
            title: t("plans.solo"),
            type: "solo",
            subtitle: t("plans.soloDesc"),
            price: '9€',
            features: [
                t("plans.featureTimeTracking"),
                t("plans.featureProjectManagement"),
                t("plans.featureReporting"),
            ],
        },
        {
            index: 2,
            title: t("plans.business"),
            type: "business",
            subtitle: t("plans.businessDesc"),
            price: '29€',
            features: [
                t("plans.featureTimeTracking"),
                t("plans.featureProjectManagement"),
                t("plans.featureReporting"),
            ],
        },
        {
            index: 3,
            title: t("plans.premium"),
            type: "premium",
            subtitle: t("plans.premiumDesc"),
            price: '49€',
            features: [
                t("plans.featureTimeTracking"),
                t("plans.featureProjectManagement"),
                t("plans.featureReporting"),
            ],
        },
    ];

    return (
        <Box
            component="section"
            sx={{
                bgcolor: 'white',
                pt: { xs: 8, md: 12 },
                pb: { xs: 10, md: 16 },
                px: { xs: 4, sm: 8, lg: 16 },
                width: '100%',
                maxWidth: 'lg',
                mx: 'auto',
            }}
        >
            {/* Main Title */}
            <Typography
                id="pricing"       
                variant="h4"
                component="h2"
                textAlign="center"
                sx={{
                    fontWeight: 800,
                    color: 'grey.900',
                    mb: 3,
                    fontSize: { xs: '2rem', sm: '2.5rem' }
                }}
            >
                {t("plans.title")}
            </Typography>

            {/* Subtitle/Intro Text */}
            <Typography
                variant="body1"
                textAlign="center"
                sx={{
                    color: 'grey.700',
                    maxWidth: 700,
                    mx: 'auto',
                    mb: { xs: 8, md: 10 }
                }}
            >
                {t("plans.subtitle")}
            </Typography>

            {/* 3-Column Pricing Grid */}
            <Grid container spacing={{ xs: 4, md: 6 }} alignItems="stretch">
                {plans.map((plan, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <PricingCard plan={plan} />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default PricingSection;